import React, { useState } from 'react';
import { format } from 'date-fns';
import { ChevronDown, ChevronRight } from 'lucide-react';

const CheckInsDisplay = ({ checkIns, refreshCheckIns, currentEvent }) => {
  const [expandedRows, setExpandedRows] = useState(new Set());
  
  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'MMM d, yyyy h:mm a');
    } catch {
      return dateString;
    }
  };
  
  // Toggle row expansion
  const toggleRowExpansion = (checkInId) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(checkInId)) {
      newExpandedRows.delete(checkInId);
    } else {
      newExpandedRows.add(checkInId);
    }
    setExpandedRows(newExpandedRows);
  };

  if (!checkIns || checkIns.length === 0) {
    return <div className="bg-white shadow rounded-lg p-6 text-center">No check-ins found</div>;
  }
  
  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <h2 className="text-xl font-bold text-gray-900">Event Check-ins</h2>
        <p className="text-sm text-gray-600 mt-1">{checkIns.length} check-ins total</p>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8"></th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Zone</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ticket Type</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Check-in Time</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {checkIns.map((checkIn) => (
              <React.Fragment key={checkIn.id}>
                {/* Main row */}
                <tr className="hover:bg-gray-50">
                  <td className="px-2 py-4 whitespace-nowrap">
                    <button
                      onClick={() => toggleRowExpansion(checkIn.id)}
                      className="text-gray-400 hover:text-gray-600 focus:outline-none"
                    >
                      {expandedRows.has(checkIn.id) ? (
                        <ChevronDown size={16} />
                      ) : (
                        <ChevronRight size={16} />
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {checkIn.submission.first_name} {checkIn.submission.last_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {checkIn.submission.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {checkIn.submission.phone || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex flex-col">
                      <span className="font-medium">{checkIn.zone?.name}</span>
                      <span className="text-xs text-gray-400">({checkIn.zone?.code})</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {checkIn.submission.ticket_type || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(checkIn.created_at)}
                  </td>
                </tr>
                
                {/* Expanded row */}
                {expandedRows.has(checkIn.id) && (
                  <tr className="bg-gray-50">
                    <td colSpan="7" className="px-8 py-4">
                      <div className="border border-gray-200 rounded-lg p-4 bg-white">
                        <h4 className="text-sm font-medium text-gray-900 mb-3">Additional Details</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <p className="text-xs text-gray-500">Check-in ID</p>
                            <p className="text-sm font-mono text-gray-900 break-all">{checkIn.id}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Submission ID</p>
                            <p className="text-sm font-mono text-gray-900 break-all">{checkIn.submission.id}</p>
                          </div>
                          <div>
                            <p className="text-xs text-gray-500">Submission Date</p>
                            <p className="text-sm text-gray-900">{formatDate(checkIn.submission.submitted_at)}</p>
                          </div>
                          {checkIn.note && (
                            <div className="md:col-span-3">
                              <p className="text-xs text-gray-500">Check-in Note</p>
                              <p className="text-sm text-gray-900">{checkIn.note}</p>
                            </div>
                          )}
                          {!checkIn.note && (
                            <div className="md:col-span-3">
                              <p className="text-xs text-gray-500 italic">No check-in note provided</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CheckInsDisplay;